import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Papa from 'papaparse';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { BsBoxArrowInDown as UploadIcon } from '@react-icons/all-files/bs/BsBoxArrowInDown';
import { BsCheck as CheckIcon } from '@react-icons/all-files/bs/BsCheck';
import { BsFillExclamationCircleFill } from '@react-icons/all-files/bs/BsFillExclamationCircleFill';
import map from 'lodash.map';
import find from 'lodash.find';
import isarray from 'lodash.isarray';
import * as XLSX from 'xlsx';
import { history, getPath, store, getValueOfAttribut } from 'helpers';
import { userService } from 'services';
import { cartActions } from 'actions/Cart';
import ReactTable from 'components/Core/ReactTable/ReactTable';
import { ButtonPrimary } from 'components/Core/Button/Button';
import Alert from 'components/Alert/Alert';
import { selectCartError, selectCurrentCartId, selectCurrentPreorderCartId } from 'selectors/cart';
import { selectCurrentPlatformId, selectCurrentPlatform } from 'selectors/platform';
import { selectCurrentUser } from 'selectors/user';
import { cartConstants } from 'constants/Cart.constants';
import { platformConstants } from 'constants/Platform.constants';
import './OrderFromCsv.scss';
import ExampleXLSX from 'styles/assets/files/import_commande_exemple.xlsx';
import Spinner from 'components/Core/Spinner/Spinner';

// Column-header aliases shared by every parser (CSV, simple XLSX, customer
// catalog XLSX) so a header recognised in one format is recognised in all.
// Compared against trimmed, lower-cased header text.
const eanHeaderAliases = ['ean', 'ean13', 'code ean', 'gencod', 'gencode', 'codeean'];
const referenceHeaderAliases = [
  'référence',
  'réference',
  'reference',
  'code interne',
  'code article',
];
const quantityHeaderAliases = [
  'quantité',
  'quantités',
  'quantity',
  'colis',
  'nb de colis',
  'nombre de colis souhaité',
];

// EAN column headers are often decorated in real files, e.g. "GENCOD (EAN)".
// Match by substring (unlike reference/quantity, which stay exact) so these
// variants are still recognised across all three parsers.
const isEanHeader = (text) => {
  if (text === undefined || text === null) return false;
  const norm = text.toString().trim().toLowerCase();
  return eanHeaderAliases.some((alias) => norm.includes(alias));
};

// Match an item to a row in `collection` by reference, then by EAN. A blank
// reference is never used for matching, so EAN-only rows (reference === '')
// don't all collide onto the first blank-reference row via lodash find.
const findByReferenceThenEan = (collection, item) => {
  let elm = item.reference ? find(collection, { reference: item.reference }) : undefined;
  if (!elm && item.ean) {
    elm = find(collection, { ean: item.ean });
  }
  return elm;
};

const OrderFromCsv = ({
  ocrOrderPdf,
  createCart,
  user,
  platformId,
  platform,
  cartId,
  preorderCartId,
  error,
}) => {
  const [csvfile, setCsvfile] = useState(undefined);
  const [itemsReactTableImport, setItemsReactTableImport] = useState(undefined);
  const [referencesExist, setReferencesExist] = useState(undefined);
  const [visibility, setVisibility] = useState('visible');
  const [isloading, setIsloading] = useState(false);
  const [disable, setDisable] = useState(true);
  const [errorImport, setErrorImport] = useState(false);
  const [referencesRapport, setReferencesRapport] = useState(undefined);
  const [quantityRapport, setQuantityRapport] = useState(undefined);
  const [inputKey, setInputKey] = useState('');

  // Get minimum reference length from platform attributes
  // Get platform attributes or use empty object if platform is undefined
  const { attributs } = platform || {};

  // Get the minimum reference length from platform attributes
  const minLengthReference = getValueOfAttribut(
    attributs,
    platformConstants.KEY_MIN_LENGTH_REFERENCE_ARTICLE_IMPORT
  );

  // Boolean flag: true if minLengthReference is greater than 0, false otherwise
  const hasMinLengthReference = Number(minLengthReference) > 0;

  // Left-pad a reference to the platform minimum length. Never pads an empty
  // value (EAN-only rows have no internal reference) and never touches EANs.
  const padReference = (value) => {
    const ref = (value === undefined || value === null ? '' : value).toString().trim();
    if (ref && hasMinLengthReference && ref.length < minLengthReference) {
      return ref.padStart(minLengthReference, '0');
    }
    return ref;
  };

  useEffect(() => {
    if (csvfile !== undefined) {
      setIsloading(true);
      const re = /(?:\.([^.]+))?$/;
      const ext = re.exec(csvfile.name)[1];
      console.log('[OrderFromCsv] file selected:', csvfile.name, '| parsed ext:', ext);

      if (ext === 'csv') {
        console.log('[OrderFromCsv] -> CSV branch');
        Papa.parse(csvfile, {
          error(err) {
            // No error handler existed before: a parse failure left isloading stuck true (infinite spinner)
            console.error('[OrderFromCsv] Papa.parse error -> spinner would hang:', err);
            setIsloading(false);
          },
          complete(results) {
            const itemsResult = results.data;
            const arrDatas = [];
            // Detect columns from the header row so an optional EAN column is
            // supported. Falls back to the historical positional layout
            // (col0 = reference, col1 = quantity) when headers aren't recognized.
            const headerCells = (itemsResult[0] || []).map((c) =>
              (c === undefined || c === null ? '' : c).toString().trim().toLowerCase()
            );
            const findIdx = (names) => headerCells.findIndex((c) => names.includes(c));
            const eanIdx = headerCells.findIndex((c) => isEanHeader(c));
            const refDetected = findIdx(referenceHeaderAliases);
            const qtyDetected = findIdx(quantityHeaderAliases);
            const refIdx = refDetected >= 0 ? refDetected : 0;
            const qtyIdx = qtyDetected >= 0 ? qtyDetected : eanIdx >= 0 ? 2 : 1;
            const itemsResultSlice = itemsResult.slice(1);
            for (let i = 0; itemsResultSlice[i]; i += 1) {
              const cols = itemsResultSlice[i];
              const reference = padReference(cols[refIdx]);
              const ean =
                eanIdx >= 0 && cols[eanIdx] !== undefined && cols[eanIdx] !== null
                  ? cols[eanIdx].toString().trim()
                  : '';
              const quantityCsv = Number.parseInt(cols[qtyIdx], 10);
              if ((reference || ean) && !Number.isNaN(quantityCsv) && quantityCsv > 0) {
                arrDatas.push({
                  reference,
                  ean,
                  quantity: quantityCsv.toString(),
                });
              }
            }
            console.log('[OrderFromCsv] CSV parse complete, rows kept:', arrDatas.length);
            setItemsReactTableImport(arrDatas);
            setIsloading(false);
          },
        });
      } else if (ext === 'xlsx' || ext === 'xls') {
        console.log('[OrderFromCsv] -> XLSX/XLS branch');
        const reader = new FileReader();
        reader.readAsBinaryString(csvfile);
        reader.onerror = (err) => {
          // No error handler existed before: a read failure left isloading stuck true (infinite spinner)
          console.error('[OrderFromCsv] FileReader error -> spinner would hang:', err);
          setIsloading(false);
        };
        reader.onload = (e) => {
          const data = e.target.result;
          const workbook = XLSX.read(data, { type: 'binary' });

          const sheetIndex = workbook.SheetNames.length > 1 ? 1 : 0;
          const sheetName = workbook.SheetNames[sheetIndex];
          const sheet = workbook.Sheets[sheetName];
          const parsedData = XLSX.utils.sheet_to_json(sheet);

          // Check xlsx file format (simple order | customer catalog order)
          // Customer catalog order type has __EMPTY_X entries
          const hasUnderscoredEmptyKeys = parsedData.find((item) => item.__EMPTY_1);

          // clear array
          const arrDatas = [];

          // Customer catalog order type
          if (hasUnderscoredEmptyKeys) {
            let referenceKey = '';
            let quantityKey = '';
            let eanKey = '';

            parsedData.forEach((line) => {
              const findReferenceKey = Object.keys(line).find(
                (key) => line[key] === 'Code article'
              );
              const findQuantityKey = Object.keys(line).find(
                (key) => line[key] === 'Nombre de colis souhaité'
              );
              const findEanKey = Object.keys(line).find((key) => isEanHeader(line[key]));

              // Column line
              if (findReferenceKey !== undefined && findQuantityKey !== undefined) {
                referenceKey = findReferenceKey;
                quantityKey = findQuantityKey;
                eanKey = findEanKey !== undefined ? findEanKey : '';
                // Go to next iteration straight away (items rows)
                return;
              }

              // Product line (accept an EAN-only row when no internal code)
              const hasRef = referenceKey && line[referenceKey] !== undefined;
              const hasEan = eanKey && line[eanKey] !== undefined;
              if ((hasRef || hasEan) && line[quantityKey] !== undefined) {
                arrDatas.push({
                  reference: hasRef ? padReference(line[referenceKey]) : '',
                  ean: hasEan ? line[eanKey].toString().trim() : '',
                  quantity: line[quantityKey].toString().trim(),
                });
              }
            });
          }

          // Simple order type
          else {
            map(parsedData, (a) => {
              const lineObject = {};
              Object.entries(a).forEach(([key, val]) => {
                if (key && val) {
                  const keyTrim = key.trim().toLowerCase();
                  if (referenceHeaderAliases.includes(keyTrim)) {
                    lineObject.reference = val;
                  }
                  if (isEanHeader(keyTrim)) {
                    lineObject.ean = val;
                  }
                  if (quantityHeaderAliases.includes(keyTrim)) {
                    lineObject.quantity = val;
                  }
                }
              });

              // Generate final datas (accept an EAN-only row when no internal code)
              if ((lineObject.reference || lineObject.ean) && lineObject.quantity) {
                const quantityXls = Number.parseInt(lineObject.quantity, 10);
                if (!Number.isNaN(quantityXls) && quantityXls > 0) {
                  arrDatas.push({
                    reference: padReference(lineObject.reference),
                    ean: lineObject.ean ? lineObject.ean.toString().trim() : '',
                    quantity: quantityXls.toString().trim(),
                  });
                }
              }
            });
          }

          console.log('[OrderFromCsv] XLSX parse complete, rows kept:', arrDatas.length);
          setItemsReactTableImport(arrDatas);
          setIsloading(false);
        };
      } else {
        // No branch matched (e.g. uppercase extension like .CSV / .XLSX): isloading stays true forever
        console.error(
          '[OrderFromCsv] Unrecognized extension "%s" -> no parser ran, spinner would hang. Clearing isloading.',
          ext
        );
        setIsloading(false);
      }
    }
  }, [csvfile]);

  const importCsvColumns = React.useMemo(() => [
    {
      id: 'reference',
      Header: 'Référence',
      accessor: (item) => item.reference,
    },
    {
      id: 'ean',
      Header: 'EAN',
      accessor: (item) => item.ean || '',
    },
    {
      id: 'quantity',
      Header: 'Nb de colis',
      accessor: (item) => item.quantity,
    },
  ]);

  const responseSwitch = (reason, type) => {
    let reasonText = '';
    let reasonTextHtml = '';
    switch (reason) {
      case 1:
        reasonText = cartConstants.REASON_UNKNOW;
        reasonTextHtml = <i style={{ color: 'red' }}>{cartConstants.REASON_UNKNOW}</i>;
        break;
      case 2:
        reasonText = cartConstants.REASON_REF_NOT_FOUND;
        reasonTextHtml = <i style={{ color: 'red' }}>{cartConstants.REASON_REF_NOT_FOUND}</i>;
        break;
      case 3:
        reasonText = cartConstants.REASON_REF_OUT_OF_STOCK;
        reasonTextHtml = <i style={{ color: 'red' }}>{cartConstants.REASON_REF_OUT_OF_STOCK}</i>;
        break;
      case 4:
        reasonText = cartConstants.REASON_REF_NOT_IN_ASSORTIMENT;
        reasonTextHtml = (
          <i style={{ color: 'red' }}>{cartConstants.REASON_REF_NOT_IN_ASSORTIMENT}</i>
        );
        break;
      case 10:
        reasonText = 'Importé';
        reasonTextHtml = <i style={{ color: 'green' }}>Importé</i>;
        break;
      case 11:
        reasonText = 'Partiellement importé';
        reasonTextHtml = <i style={{ color: 'orange' }}>Partiellement importé</i>;
        break;
      case 12:
        reasonText = 'Non importé';
        reasonTextHtml = <i style={{ color: 'red' }}>Non importé</i>;
        break;
      default:
        reasonTextHtml = '';
    }
    if (type === 'html') {
      return reasonTextHtml;
    }
    return reasonText;
  };

  const ARTICLE_NOT_FOUND = 'article non trouvé';
  const EAN_NOT_FOUND = 'EAN non trouvé';

  // Extract the brand ("Marque") from a rapport item, trying the field names the API may use.
  // When no brand is found, leave the field empty (rather than "article non trouvé").
  const getMarque = (item) => {
    const marque =
      item.manufacturer_name ||
      item.item_manufacturer_name ||
      (item.manufacturer && item.manufacturer.name) ||
      item.brand_name ||
      item.brand ||
      item.marque;
    return marque || '';
  };

  // Extract the article label ("Libellé article") from a rapport item.
  const getLibelle = (item) => {
    const libelle =
      item.label ||
      item.libelle ||
      item.item_label ||
      item.item_name ||
      item.designation ||
      item.wording ||
      item.product_name ||
      item.name;
    return libelle || ARTICLE_NOT_FOUND;
  };

  // Match a report row back to its source file row by reference, then by EAN
  // (the API may have resolved the reference from the EAN at import time, so the
  // returned reference can differ from the one originally typed in the file).
  const findImportedRow = (item) => findByReferenceThenEan(itemsReactTableImport, item);

  // Extract the EAN for a rapport row. When the import is done by internal code
  // (reference), the backend resolves and returns the matching EAN — try every
  // field name it may use. Fall back to the EAN of the matched source file row
  // (EAN-only imports). If no EAN corresponds to the code, show "EAN non trouvé".
  const getEan = (item) => {
    const apiEan =
      item.ean ||
      item.ean13 ||
      item.code_ean ||
      item.codeean ||
      item.gencod ||
      item.gencode ||
      item.barcode ||
      item.item_ean;
    if (apiEan) {
      return apiEan.toString();
    }
    const elm = findImportedRow(item);
    if (elm && elm.ean) {
      return elm.ean;
    }
    return EAN_NOT_FOUND;
  };

  const rapportCsvColumns = React.useMemo(() => [
    {
      id: 'reference',
      Header: 'Référence',
      accessor: (item) => item.reference,
    },
    {
      id: 'ean',
      Header: 'EAN',
      accessor: (item) => getEan(item),
    },
    {
      id: 'marque',
      Header: 'Marque',
      accessor: (item) => getMarque(item),
    },
    {
      id: 'libelle',
      Header: 'Libellé article',
      accessor: (item) => getLibelle(item),
    },
    {
      id: 'quantity',
      Header: 'Nb de colis',
      accessor: (item) => {
        const elm = findImportedRow(item);
        const quantityRef = item.quantity ? item.quantity : 0;
        const elmQuantity = elm ? elm.quantity : 0;
        return `${quantityRef} / ${elmQuantity}`;
      },
      className: 'text-center',
    },
    {
      id: 'reason',
      Header: "Statut d'import",
      accessor: (item) => responseSwitch(item.reason, 'html'),
    },
  ]);

  const handleChange = (e) => {
    setCsvfile(e.target.files[0]);
    setDisable(false);
  };

  const handlResetImport = () => {
    setCsvfile(undefined);
    setItemsReactTableImport(undefined);
    setReferencesExist(undefined);
    setVisibility('visible');
    setIsloading(false);
    setDisable(true);
    setErrorImport(false);
    setReferencesRapport(undefined);
    setQuantityRapport(undefined);
    const randomString = Math.random().toString(36);
    setInputKey(randomString);
  };

  const handleDownloadRapport = () => {
    let arrResultRapport = [];
    map(referencesRapport, (a) => {
      let arrRef = [];
      const ref = findImportedRow(a);
      arrRef = [responseSwitch(a.reason, 'text'), ...arrRef];
      arrRef = [a.quantity ? a.quantity : 0, ...arrRef];
      arrRef = [ref ? ref.quantity : 0, ...arrRef];
      arrRef = [getLibelle(a), ...arrRef];
      arrRef = [getMarque(a), ...arrRef];
      arrRef = [getEan(a), ...arrRef];
      arrRef = [ref && ref.reference ? ref.reference : a.reference, ...arrRef];
      arrResultRapport = [arrRef, ...arrResultRapport];
    });
    const csv = Papa.unparse({
      delimiter: ';',
      fields: [
        'Référence',
        'EAN',
        'Marque',
        'Libellé article',
        'Nb de colis demandé',
        'Nb de colis importé',
        "Statut d'import",
      ],
      data: arrResultRapport.reverse(),
      download: true,
    });

    const csvData = new Blob([csv]);
    const csvURL = URL.createObjectURL(csvData, { type: 'text/plain' });
    const linkDownload = document.createElement('a');
    linkDownload.href = csvURL;
    linkDownload.setAttribute('download', 'rapport-import.csv');
    linkDownload.click();
  };

  const handleDownloadRapportXls = () => {
    /* generate worksheet and workbook */
    let arrResultRapport = [];
    map(referencesRapport, (a) => {
      let arrRef = [];
      const ref = findImportedRow(a);
      arrRef = [responseSwitch(a.reason, 'text'), ...arrRef];
      arrRef = [a.quantity ? a.quantity : 0, ...arrRef];
      arrRef = [ref ? ref.quantity : 0, ...arrRef];
      arrRef = [getLibelle(a), ...arrRef];
      arrRef = [getMarque(a), ...arrRef];
      arrRef = [getEan(a), ...arrRef];
      arrRef = [ref && ref.reference ? ref.reference : a.reference, ...arrRef];
      arrResultRapport = [arrRef, ...arrResultRapport];
    });
    const datas = arrResultRapport.reverse();
    const worksheet = XLSX.utils.json_to_sheet(datas);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Dates');

    /* fix headers */
    XLSX.utils.sheet_add_aoa(
      worksheet,
      [
        [
          'Référence',
          'EAN',
          'Marque',
          'Libellé article',
          'Nb de colis demandé',
          'Nb de colis importé',
          "Statut d'import",
        ],
      ],
      { origin: 'A1' }
    );

    /* set column width */
    worksheet['!cols'] = [
      { wch: 12 },
      { wch: 15 },
      { wch: 20 },
      { wch: 30 },
      { wch: 15 },
      { wch: 15 },
      { wch: 23 },
    ];

    /* create an XLSX file and try to save to rapport.xlsx */
    XLSX.writeFile(workbook, 'rapport.xlsx', { compression: true });
  };

  const importCSV = () => {
    console.log(
      '[OrderFromCsv] importCSV clicked, calling createCart with',
      itemsReactTableImport && itemsReactTableImport.length,
      'references. Waiting for API...'
    );
    setIsloading(true);
    setDisable(true);
    createCart(
      user,
      platformId,
      cartId,
      preorderCartId,
      itemsReactTableImport,
      setReferencesExist,
      setReferencesRapport,
      setQuantityRapport,
      setIsloading,
      setVisibility,
      setErrorImport
    );
  };
  const goToDashboard = () => {
    history.push(userService.getHomepagePath());
  };

  const goToCart = (id) => {
    if (id) {
      history.push(getPath('cart').replace(':id', id).replace('/:temperature?', ''));
    }
  };

  const limitReferences = 2401;

  return (
    <div className="page-order-from-csv">
      <h2 className="csv-title">Créez votre panier en important un fichier CSV</h2>
      <div className="csv-col">
        <div className="csv-description">
          <p>
            Vous pouvez créer votre panier en utilisant un fichier au format CSV ou excel (xls,
            xlsx).
            <br />
            <br />
            Le fichier doit comporter au moins deux informations :<br />
            la <b>référence</b> de l’article (code interne) et la <b>quantité</b> colis.
            <br />
            Vous pouvez ajouter une colonne <b>EAN</b> : si la référence n’est pas reconnue,
            l’article est alors recherché par son code EAN.
            <br />
            <br />
            Le fichier ne doit pas dépasser <b>{limitReferences - 1}</b> codes articles.
            <br />
            <br />
            Pour créer un fichier CSV, utilisez le menu “Enregistrer au format CSV” d’Excel.
            <br />
            <br />
            Vous pouvez télécharger un modèle de fichier{' '}
            <Link to={ExampleXLSX} target="_blank" download>
              ici
            </Link>
            .
          </p>
        </div>
        <div className="csv-form">
          <div className="orderFromCsv__errors">
            {error && error.message && <Alert text={error.message} />}
          </div>
          <div className="csv-form-container">
            <div className="csv-file-input-container">
              <input
                className="csv-file-input"
                type="file"
                key={inputKey || ''}
                // ref={(input) => {
                //   const fileInput = input;
                // }}
                name="file"
                placeholder={null}
                onChange={handleChange}
                accept=".xls,.xlsx,.csv,text/csv"
              />
            </div>
            {csvfile === undefined && (
              <div className="csv-message-input">
                <UploadIcon />
                <span>Déposer ou Sélectionner un fichier</span>
              </div>
            )}
            {csvfile !== undefined && (
              <>
                {isloading && (
                  <div className="is-loading">
                    <Spinner />
                  </div>
                )}
                {itemsReactTableImport &&
                itemsReactTableImport.length > 0 &&
                itemsReactTableImport.length < limitReferences ? (
                  <div className="csv-message-input-2">
                    <CheckIcon />
                    <span>Votre fichier "{csvfile.name}" est prêt à être importé</span>
                    <div className={`bloc-btn ${visibility}`}>
                      <ButtonPrimary disabled={disable} onClick={importCSV}>
                        Importer !
                      </ButtonPrimary>
                      <ButtonPrimary onClick={handlResetImport}>Annuler</ButtonPrimary>
                    </div>
                  </div>
                ) : (
                  <div className="csv-message-input-2">
                    <BsFillExclamationCircleFill />
                    <span>Votre fichier "{csvfile.name}" ne peut être importé</span>
                    <div className={`bloc-btn ${visibility}`}>
                      <ButtonPrimary onClick={handlResetImport}>
                        Importer un nouveau fichier
                      </ButtonPrimary>
                    </div>
                  </div>
                )}
              </>
            )}
            {referencesRapport !== undefined && (
              <div className="file-imported">
                <CheckIcon />
                <span>Votre fichier "{csvfile.name}" à été importé</span>
                <br />
                <br />
                <div className="bloc-button-flex">
                  <ButtonPrimary onClick={handlResetImport}>
                    Importer un nouveau fichier
                  </ButtonPrimary>
                  {/* <ButtonPrimary onClick={goToDashboard}>Catalogue</ButtonPrimary> */}
                </div>
              </div>
            )}
            {errorImport === true && (
              <div className="file-imported-error">
                <BsFillExclamationCircleFill />
                <span>
                  Une erreur s'est produite !
                  <br />
                  Votre fichier "{csvfile.name}" n'a pu être importé
                </span>
                <br />
                <br />
                <div className="bloc-button-flex">
                  <ButtonPrimary onClick={handlResetImport}>
                    Importer un nouveau fichier
                  </ButtonPrimary>
                  {/* <ButtonPrimary onClick={goToDashboard}>Catalogue</ButtonPrimary> */}
                </div>
              </div>
            )}
            {isloading && (
              <div className="is-loading">
                <Spinner />
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="csv-result">
        {referencesRapport !== undefined ? (
          <div className="csv-rapport">
            <div className="csv-content">
              <ReactTable
                columns={rapportCsvColumns}
                data={referencesRapport}
                isLoading={!referencesRapport}
              />
            </div>
            {quantityRapport !== undefined && (
              <div className="text-rapport">
                {quantityRapport && quantityRapport.quantityImported > 1 && (
                  <span className="green">
                    {quantityRapport.quantityImported} références ont été importées.
                  </span>
                )}
                {quantityRapport && quantityRapport.quantityImported === 1 && (
                  <span className="green">
                    {quantityRapport.quantityImported} référence a été importée.
                  </span>
                )}
                {quantityRapport && quantityRapport.quantityPartialImported > 1 && (
                  <span className="orange">
                    {quantityRapport.quantityPartialImported} références ont été partiellement
                    importées.
                  </span>
                )}
                {quantityRapport && quantityRapport.quantityPartialImported === 1 && (
                  <span className="orange">
                    {quantityRapport.quantityPartialImported} référence a été partiellement
                    importée.
                  </span>
                )}
                {quantityRapport && quantityRapport.quantityNotImported > 1 && (
                  <span className="red">
                    {quantityRapport.quantityNotImported} références n'ont pas été importées.
                  </span>
                )}
                {quantityRapport && quantityRapport.quantityNotImported === 1 && (
                  <span className="red">
                    {quantityRapport.quantityNotImported} référence n'a pas été importée.
                  </span>
                )}
                <div className="download-rapport">
                  <ButtonPrimary onClick={handleDownloadRapport}>
                    Télécharger le rapport csv
                  </ButtonPrimary>
                  <ButtonPrimary onClick={handleDownloadRapportXls}>
                    Télécharger le rapport xls
                  </ButtonPrimary>
                </div>
                <div className="go-to-cart">
                  <ButtonPrimary
                    onClick={() => {
                      goToCart(cartId);
                    }}
                  >
                    Aller au panier
                  </ButtonPrimary>
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            {itemsReactTableImport !== undefined &&
              itemsReactTableImport.length >= limitReferences && (
                <div className="csv-import">
                  <div className="nb-refs">
                    <b>{itemsReactTableImport.length}</b>{' '}
                    {itemsReactTableImport.length > 0 ? `Références` : `Référence`}
                  </div>
                  <div className="csv-content error-max">
                    <p>Le maximum de codes articles à importer est de {limitReferences - 1}.</p>
                  </div>
                </div>
              )}
            {itemsReactTableImport !== undefined &&
              itemsReactTableImport.length < limitReferences && (
                <div className="csv-import">
                  <div className="nb-refs">
                    <b>{itemsReactTableImport.length}</b>{' '}
                    {itemsReactTableImport.length > 0 ? `Références` : `Référence`} à importer
                  </div>
                  <div className="csv-content">
                    <ReactTable
                      columns={importCsvColumns}
                      data={itemsReactTableImport}
                      isLoading={!itemsReactTableImport}
                    />
                  </div>
                </div>
              )}
          </>
        )}
        <div className="clearfix" />
      </div>
    </div>
  );
};

const mapDispatchToProps = (dispatch) => ({
  createCart: async (
    user,
    platformId,
    cartId,
    preorderCartId,
    itemsByReference,
    setReferencesExist,
    setReferencesRapport,
    setQuantityRapport,
    setIsloading,
    setVisibility,
    setErrorImport,
    cart,
    updateCart
  ) => {
    // One id shared by all 4 backend calls of this import (add + report, order +
    // preorder) so the EAN-resolution logs of a single import correlate via the
    // X-Request-Id header (backend logs it as requestId).
    const importId = `import-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const [result1, result2] = await Promise.all([
      cartActions.renewCurrentCartWithReferences(
        dispatch,
        platformId,
        cartId,
        itemsByReference,
        cartConstants.CART_STATUS_CURRENT,
        importId
      ),
      cartActions.renewCurrentCartWithReferences(
        dispatch,
        platformId,
        preorderCartId,
        itemsByReference,
        cartConstants.CART_STATUS_CURRENT_PREORDER,
        importId
      ),
      cartActions.updateCart(cart, {}),
    ]);
    if (result1 && result1.id && result2 && result2.id) {
      const [resultReferencesExist, resultReferencesExistPreorder] = await Promise.all([
        cartActions.renewCurrentCartWithReferencesExist(
          dispatch,
          {
            id: result1.id,
            items_by_reference: itemsByReference,
          },
          importId
        ),
        cartActions.renewCurrentCartWithReferencesExist(
          dispatch,
          {
            id: result2.id,
            items_by_reference: itemsByReference,
          },
          importId
        ),
      ]);
      if (isarray(resultReferencesExist) && isarray(resultReferencesExistPreorder)) {
        let resultNotInCart = [];
        let resultIsInCart = [];
        map(resultReferencesExist, (a) => {
          // Pair the order row with its preorder row by reference, then by EAN
          // (an EAN may resolve to a different reference per cart); a blank
          // reference never matches, and a missing pair defaults to an empty
          // match so divergent reference sets never crash the reduce.
          const ref = findByReferenceThenEan(resultReferencesExistPreorder, a) || {
            is_in_cart: 0,
            quantity: 0,
          };
          if (a.is_in_cart === 0 && ref.is_in_cart === 0) {
            resultNotInCart = [a, ...resultNotInCart];
          } else {
            resultIsInCart = [a, ...resultIsInCart];
          }
        });

        let resultRapportCart = [];
        const quantityRefs = {
          quantityImported: 0,
          quantityNotImported: 0,
          quantityPartialImported: 0,
        };
        map(resultReferencesExist.reverse(), (a) => {
          const arrRef = a;
          // Pair the order row with its preorder row by reference, then by EAN
          // (an EAN may resolve to a different reference per cart); a blank
          // reference never matches, and a missing pair defaults to an empty
          // match so divergent reference sets never crash the reduce.
          const ref = findByReferenceThenEan(resultReferencesExistPreorder, a) || {
            is_in_cart: 0,
            quantity: 0,
          };
          if (a.is_in_cart === 0 && ref.is_in_cart === 0) {
            arrRef.quantity = 0;
            const q = quantityRefs.quantityNotImported + 1;
            quantityRefs.quantityNotImported = q;
            resultRapportCart = [arrRef, ...resultRapportCart];
          } else {
            // Preorder-only articles return Marque/Libellé (and EAN) on the
            // preorder response (`ref`), not the order response (`a`) which
            // comes back "not in assortment" (reason 4) with no product data.
            // The report row is built from `a`, so copy the display fields from
            // `ref` when missing — otherwise the Marque/Libellé columns show
            // "article non trouvé" even though the item was imported.
            if (ref) {
              [
                'manufacturer_name',
                'item_manufacturer_name',
                'manufacturer',
                'brand_name',
                'brand',
                'marque',
                'label',
                'libelle',
                'item_label',
                'item_name',
                'designation',
                'wording',
                'product_name',
                'name',
                'ean',
                'ean13',
                'code_ean',
                'codeean',
                'gencod',
                'gencode',
                'barcode',
                'item_ean',
              ].forEach((k) => {
                if (
                  (arrRef[k] === undefined || arrRef[k] === null || arrRef[k] === '') &&
                  ref[k] != null
                ) {
                  arrRef[k] = ref[k];
                }
              });
            }
            // Match the source row by reference, then by EAN (the reference may
            // have been resolved from the EAN at import time, so a's typed
            // reference can differ or be empty).
            let refQ = findByReferenceThenEan(itemsByReference, a);
            if (!refQ && /^0\d+/.test(a.reference)) {
              // Remove leading zeros and try to find again
              const referenceWithoutLeadingZeros = a.reference.replace(/^0+/, '');
              refQ = find(itemsByReference, { reference: referenceWithoutLeadingZeros });
            }
            if (refQ) {
              const quantityAsked = Number.parseInt(refQ.quantity, 10);
              const quantityOrder = Number.parseInt(a.quantity, 10)
                ? Number.parseInt(a.quantity, 10)
                : 0;
              const quantityPreOrder = Number.parseInt(ref.quantity, 10)
                ? Number.parseInt(ref.quantity, 10)
                : 0;
              if (
                quantityOrder + quantityPreOrder < quantityAsked &&
                quantityOrder + quantityPreOrder !== 0
              ) {
                arrRef.reason = 11;
                const q = quantityRefs.quantityPartialImported + 1;
                quantityRefs.quantityPartialImported = q;
              } else if (quantityOrder + quantityPreOrder === quantityAsked) {
                arrRef.reason = 10;
                const q = quantityRefs.quantityImported + 1;
                quantityRefs.quantityImported = q;
              } else {
                arrRef.reason = 12;
                const q = quantityRefs.quantityNotImported + 1;
                quantityRefs.quantityNotImported = q;
              }
              arrRef.quantity = quantityOrder + quantityPreOrder;
              resultRapportCart = [arrRef, ...resultRapportCart];
            }
          }
        });
        // Discovery log: reveals the real field names returned per reference so the
        // Marque / Libellé article accessors can be confirmed against the API payload.
        console.log(
          '[OrderFromCsv] rapport item keys:',
          resultRapportCart[0] && Object.keys(resultRapportCart[0]),
          '| sample:',
          resultRapportCart[0]
        );
        setReferencesRapport(resultRapportCart);
        setQuantityRapport(quantityRefs);
        setVisibility('hidden');
        setReferencesExist(resultNotInCart);
        setIsloading(false);
      }
    } else {
      setErrorImport(true);
      setIsloading(false);
    }
    setIsloading(false);
    return 'error';
  },
});

const mapStateToProps = (state) => ({
  platformId: selectCurrentPlatformId(state),
  platform: selectCurrentPlatform(state),
  cartId: Number(selectCurrentCartId(state)),
  preorderCartId: Number(selectCurrentPreorderCartId(state)),
  error: selectCartError(state),
  user: selectCurrentUser(state),
});

OrderFromCsv.propTypes = {
  platformId: PropTypes.number,
  platform: PropTypes.shape({
    attributs: PropTypes.array,
  }),
  createCart: PropTypes.func,
  cartId: PropTypes.number,
  preorderCartId: PropTypes.number,
  error: PropTypes.shape({
    message: PropTypes.string,
  }),
  user: PropTypes.shape({
    has_dashboard_v2: PropTypes.bool,
  }),
};

export default connect(mapStateToProps, mapDispatchToProps)(OrderFromCsv);
